import { computed, Ref } from 'vue';
import store from '@/store';

import {
    bringGroupedToFront,
    canMoveBackward,
    canMoveForward,
    findTreeItemById,
    getGroupedSelectedItems,
    getSiblings,
    isActiveSelection,
    isValidCanvasItem,
    moveGroupedBackward,
    moveGroupedForward,
    sendGroupedToBack
} from '../tree/store/utils/elementUtils';
import type { CanvasElement, CanvasOrSelection } from '@/typings/customilyElements/canvasElements';
import type { ElementsGroup } from '@/typings/customilyElements/elementsGroup';
import type { ElementsPageEps } from '@/typings/customilyElements/elementsPageEps';
import type { LayersProps } from '../types';
import useGroupManager from '@/modules/layers/composables/useGroupManager';
import GroupManager from '@/modules/layers/services/GroupManager';

export function useElementSelection(
    props: LayersProps,
    listPageEps: Ref<ElementsPageEps[]>,
    groups: Ref<ElementsGroup[]>
) {
    const isUpdatingTree = computed(() => store.state.storeLayers?.isUpdatingTree);

    /**
     * Wait for tree updates to complete before proceeding
     */
    const waitForTreeUpdates = async (): Promise<void> => {
        if (!isUpdatingTree.value) return;

        return new Promise<void>((resolve) => {
            const checkTreeUpdate = () => {
                if (!isUpdatingTree.value) {
                    resolve();
                } else {
                    setTimeout(checkTreeUpdate, 50);
                }
            };
            checkTreeUpdate();
        });
    };

    /**
     * Set an element as active so it's marked in the layers view
     * @param elements Array of canvas elements to set as active
     */
    const setActiveElement = async (elements?: CanvasElement[]) => {
        try {
            await waitForTreeUpdates();

            if (!elements || elements.length === 0) {
                store.dispatch('storeLayers/clearSelectedItems');
                return;
            }

            const ids = elements.map((element) => element.uuid || element.id);
            store.dispatch('storeLayers/setSelectedElementByIds', { ids });
        } catch (error) {
            console.error('Error setting active element:', error);
        }
    };

    /**
     * Initialize and select the first group from pageEps
     */
    const initGroupSelectionFromPageEps = async () => {
        if (!props.canvas) return;
        props.canvas.setActiveFirstPageEps();
        props.auxCanvas?.setActiveFirstPageEps();

        // Set visibility synchronously before yielding to the browser,
        // so the canvas never renders with all layouts visible (flash).
        const mgr = new GroupManager(props.canvas);
        const defaults = mgr.getDefaultVisibleLayouts();

        if (defaults.length > 0) {
            const pageEpsElements = props.canvas.getElementsPageEps?.() || [];
            const firstEps = pageEpsElements[0];

            if (firstEps) {
                const canvasGroups = (props.canvas.elementsGroups || []) as ElementsGroup[];
                const orderedGroups = [...canvasGroups].sort((a, b) => b.zIndex - a.zIndex);
                const epsMatch = orderedGroups.find((group) => group.pageEpsId === firstEps.id && group.isLayout);

                if (epsMatch) {
                    const idx = defaults.findIndex((d) => d.parentId === epsMatch.parentId);
                    if (idx !== -1) {
                        defaults[idx] = epsMatch;
                    }
                }
            }

            mgr.setVisibility(defaults, true);
        }

        await waitForTreeUpdates();
    };

    const singleGroupSelection = () => {
        //
    };

    const moveElementForward = (element: CanvasOrSelection) => {
        const treeItems = store.state.storeLayers?.treeItems;
        if (!treeItems) {
            return;
        }

        if (isActiveSelection(element)) {
            const elements = element.getObjects();
            const groups = getGroupedSelectedItems(elements, treeItems);

            for (const group of groups) {
                moveGroupedForward(group);
            }
        } else {
            const item = findTreeItemById(treeItems, element.uuid || element.id.toString());
            if (!isValidCanvasItem(item)) {
                return;
            }

            const siblings = getSiblings(item, treeItems);
            const index = siblings.indexOf(item);
            if (!canMoveForward(index)) {
                return;
            }

            siblings.splice(index, 1);
            siblings.splice(index - 1, 0, item);
        }

        store.dispatch('storeLayers/setTreeItems', treeItems);
    };

    const moveElementBackward = (element: CanvasOrSelection) => {
        const treeItems = store.state.storeLayers?.treeItems;
        if (!treeItems) {
            return;
        }

        if (isActiveSelection(element)) {
            const elements = element.getObjects();
            const groups = getGroupedSelectedItems(elements, treeItems);

            for (const group of groups) {
                moveGroupedBackward(group);
            }
        } else {
            const item = findTreeItemById(treeItems, element.uuid || element.id.toString());
            if (!isValidCanvasItem(item)) {
                return;
            }

            const siblings = getSiblings(item, treeItems);
            const index = siblings.indexOf(item);
            if (!canMoveBackward(index, siblings.length)) {
                return;
            }

            siblings.splice(index, 1);
            siblings.splice(index + 1, 0, item);
        }

        store.dispatch('storeLayers/setTreeItems', treeItems);
    };

    const bringElementToFront = (element: CanvasOrSelection) => {
        const treeItems = store.state.storeLayers?.treeItems;
        if (!treeItems) {
            return;
        }

        if (isActiveSelection(element)) {
            const elements = element.getObjects();
            const groups = getGroupedSelectedItems(elements, treeItems);

            for (const group of groups) {
                bringGroupedToFront(group);
            }
        } else {
            const item = findTreeItemById(treeItems, element.uuid || element.id.toString());
            if (!isValidCanvasItem(item)) {
                return;
            }

            const siblings = getSiblings(item, treeItems);
            const index = siblings.indexOf(item);
            if (!canMoveForward(index)) {
                return;
            }

            siblings.splice(index, 1);
            siblings.unshift(item);
        }

        store.dispatch('storeLayers/setTreeItems', treeItems);
    };

    const sendElementToBack = (element: CanvasOrSelection) => {
        const treeItems = store.state.storeLayers?.treeItems;
        if (!treeItems) {
            return;
        }

        if (isActiveSelection(element)) {
            const elements = element.getObjects();
            const groups = getGroupedSelectedItems(elements, treeItems);

            for (const group of groups) {
                sendGroupedToBack(group);
            }
        } else {
            const item = findTreeItemById(treeItems, element.uuid || element.id.toString());
            if (!isValidCanvasItem(item)) {
                return;
            }

            const siblings = getSiblings(item, treeItems);
            const index = siblings.indexOf(item);

            if (!canMoveBackward(index, siblings.length)) {
                return;
            }

            siblings.splice(index, 1);
            siblings.push(item);
        }

        store.dispatch('storeLayers/setTreeItems', treeItems);
    };

    return {
        setActiveElement,
        singleGroupSelection,
        initGroupSelectionFromPageEps,
        moveElementForward,
        moveElementBackward,
        bringElementToFront,
        sendElementToBack
    };
}
